import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  Calculator, Download, Users, Baby, Clock, Calendar,
  CheckCircle, AlertTriangle, Info, FileText, UserCheck, ShieldAlert
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const AGE_GROUPS = [
  { id: '0-18m', label: 'Birth to 18 months', weight: 0.25 },
  { id: '18m-2.5y', label: '18 months to 2½ years', weight: 0.143 },
  { id: '2.5y', label: '2½ years', weight: 0.125 },
  { id: '3y', label: '3 years', weight: 0.10 },
  { id: '4y', label: '4 years', weight: 0.077 },
  { id: '5y+', label: '5 years and over', weight: 0.056 },
];

const RatioWorksheetPage = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [employeeChildrenCompliance, setEmployeeChildrenCompliance] = useState(null);
  const [loadingCompliance, setLoadingCompliance] = useState(true);
  const [dcfMode, setDcfMode] = useState('251'); // '251' = Group Child Care, '250' = Family Child Care
  
  // Form state
  const [formData, setFormData] = useState({
    classroom_name: 'Mixture Classroom',
    date: new Date().toLocaleDateString('en-US'),
    time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }).slice(0, 5),
    am_pm: new Date().getHours() >= 12 ? 'PM' : 'AM',
    teacher_name: '',
    teacher_hours: '',
    second_teacher_name: '',
    second_teacher_hours: '',
    children_by_age: {
      '0-18m': 0,
      '18m-2.5y': 0,
      '2.5y': 0,
      '3y': 0,
      '4y': 0,
      '5y+': 0
    }
  });

  useEffect(() => {
    fetchEmployeeChildrenCompliance();
    // Default DCF mode based on center type (family child care => DCF 250)
    (async () => {
      try {
        const s = await axios.get(`${API}/settings/center`, getAuthHeaders());
        if (s.data?.center_type === 'family') setDcfMode('250');
      } catch {}
    })();
     
  }, []);

  // DCF 250 (Family Child Care) fixed-limit compliance:
  //   - Max 8 children total
  //   - Max 3 children under age 2
  //   - Max 6 children under age 6 who are not in kindergarten
  const dcf250Check = (() => {
    const c = formData.children_by_age;
    const under2 = (c['0-18m'] || 0) + (c['18m-2.5y'] || 0); // approx: 0-2.5y counts as under-2 bucket
    const under6NonK = (c['0-18m'] || 0) + (c['18m-2.5y'] || 0) + (c['2.5y'] || 0) + (c['3y'] || 0) + (c['4y'] || 0);
    const total = Object.values(c).reduce((a, b) => a + Number(b || 0), 0);
    return {
      total,
      total_ok: total <= 8,
      under2,
      under2_ok: under2 <= 3,
      under6NonK,
      under6NonK_ok: under6NonK <= 6,
      all_ok: total <= 8 && under2 <= 3 && under6NonK <= 6,
    };
  })();

  const fetchEmployeeChildrenCompliance = async () => {
    setLoadingCompliance(true);
    try {
      const response = await axios.get(`${API}/children/employee-children-compliance`, getAuthHeaders());
      setEmployeeChildrenCompliance(response.data);
    } catch (error) {
      console.error('Error fetching employee children compliance:', error);
    } finally {
      setLoadingCompliance(false);
    }
  };

  const handleChildCountChange = (ageGroup, value) => {
    const count = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      children_by_age: {
        ...prev.children_by_age,
        [ageGroup]: Math.max(0, count)
      }
    }));
  };

  const calculateRatio = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ratio-worksheet/calculate`, formData, getAuthHeaders());
      setResult(response.data);
      toast.success('Ratio calculated successfully');
    } catch (error) {
      toast.error('Failed to calculate ratio');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.post(`${API}/ratio-worksheet/pdf`, formData, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ratio_Worksheet_${formData.classroom_name.replace(/\s+/g, '_')}_${formData.date.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getTotalChildren = () => {
    return Object.values(formData.children_by_age).reduce((sum, count) => sum + count, 0);
  };

  const getTotalWeight = () => {
    let total = 0;
    AGE_GROUPS.forEach(group => {
      total += (formData.children_by_age[group.id] || 0) * group.weight;
    });
    return total.toFixed(3);
  };

  const getQuickStaffEstimate = () => {
    const weight = parseFloat(getTotalWeight());
    if (weight <= 1.04) return 1;
    return Math.ceil(weight);
  };

  return (
    <DashboardLayout title="Staff-to-Child Ratio Worksheet">
      <div className="space-y-6" data-testid="ratio-worksheet-page">
        {/* DCF Mode Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex rounded-lg border border-border bg-white p-1" data-testid="dcf-mode-toggle">
            <button
              onClick={() => setDcfMode('251')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                dcfMode === '251' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="dcf-mode-251"
            >
              DCF 251 — Group Child Care
            </button>
            <button
              onClick={() => setDcfMode('250')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                dcfMode === '250' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="dcf-mode-250"
            >
              DCF 250 — Family Child Care
            </button>
          </div>
        </div>

        {/* Header Info */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">
                  {dcfMode === '250' ? 'DCF 250.04(6) Family Child Care Capacity' : 'DCF 251.055(2) Ratio Worksheet'}
                </h3>
                <p className="text-sm text-blue-700">
                  {dcfMode === '250' ? (
                    <>
                      For <strong>Family Child Care</strong> (licensed in a residence). Wisconsin caps total children
                      at 8, with no more than 3 under age 2 and no more than 6 non-kindergarten children under age 6.
                      Enter each age group below to check compliance.
                    </>
                  ) : (
                    <>
                      Use this worksheet for <strong>Mixture Classrooms</strong> - early arrival, late dismissal,
                      or mixed-age group situations. Enter the number of children in each age group to calculate
                      the required number of staff using the DCF numerical weight formula.
                    </>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DCF 250 Family Capacity summary */}
        {dcfMode === '250' && (
          <Card className={`border-2 ${dcf250Check.all_ok ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`} data-testid="dcf250-summary">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                {dcf250Check.all_ok ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
                DCF 250 Capacity Check
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className={`p-3 rounded-lg border ${dcf250Check.total_ok ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-xs text-muted-foreground">Total children</div>
                  <div className="text-2xl font-bold">{dcf250Check.total} <span className="text-sm text-muted-foreground">/ 8</span></div>
                  {!dcf250Check.total_ok && (
                    <div className="text-xs text-red-600 mt-1">Exceeds max 8 by {dcf250Check.total - 8}</div>
                  )}
                </div>
                <div className={`p-3 rounded-lg border ${dcf250Check.under2_ok ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-xs text-muted-foreground">Under age 2</div>
                  <div className="text-2xl font-bold">{dcf250Check.under2} <span className="text-sm text-muted-foreground">/ 3</span></div>
                  {!dcf250Check.under2_ok && (
                    <div className="text-xs text-red-600 mt-1">Exceeds max 3 by {dcf250Check.under2 - 3}</div>
                  )}
                </div>
                <div className={`p-3 rounded-lg border ${dcf250Check.under6NonK_ok ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-xs text-muted-foreground">Under age 6 (non-K)</div>
                  <div className="text-2xl font-bold">{dcf250Check.under6NonK} <span className="text-sm text-muted-foreground">/ 6</span></div>
                  {!dcf250Check.under6NonK_ok && (
                    <div className="text-xs text-red-600 mt-1">Exceeds max 6 by {dcf250Check.under6NonK - 6}</div>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Reminder: the provider&apos;s own children under age 7 count toward the total when they are present.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Section 16.5 Employee Children Compliance */}
        {!loadingCompliance && employeeChildrenCompliance && (
          <Card className={`border-2 ${employeeChildrenCompliance.is_compliant ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                {employeeChildrenCompliance.is_compliant ? (
                  <UserCheck className="w-5 h-5 text-green-600" />
                ) : (
                  <ShieldAlert className="w-5 h-5 text-red-600" />
                )}
                DCF Section 16.5 - Employee Children Compliance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    No more than <strong>40%</strong> of enrolled children can be children of employees.
                  </p>
                  <p className={`text-lg font-bold mt-1 ${employeeChildrenCompliance.is_compliant ? 'text-green-700' : 'text-red-700'}`}>
                    {employeeChildrenCompliance.employee_children_count} of {employeeChildrenCompliance.total_children} children ({employeeChildrenCompliance.percentage}%)
                  </p>
                </div>
                <div className="text-right">
                  <Badge 
                    variant={employeeChildrenCompliance.is_compliant ? "default" : "destructive"}
                    className={employeeChildrenCompliance.is_compliant ? "bg-green-600" : ""}
                  >
                    {employeeChildrenCompliance.is_compliant ? 'Compliant' : 'Non-Compliant'}
                  </Badge>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0%</span>
                  <span className="text-amber-600 font-medium">40% Limit</span>
                  <span>100%</span>
                </div>
                <div className="relative">
                  <Progress 
                    value={employeeChildrenCompliance.percentage} 
                    className={`h-3 ${employeeChildrenCompliance.is_compliant ? '[&>div]:bg-green-500' : '[&>div]:bg-red-500'}`}
                  />
                  {/* 40% marker */}
                  <div className="absolute top-0 bottom-0 left-[40%] w-0.5 bg-amber-500" />
                </div>
              </div>
              
              {/* Employee Children List (if any) */}
              {employeeChildrenCompliance.employee_children?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Employee Children:</p>
                  <div className="grid gap-2">
                    {employeeChildrenCompliance.employee_children.map((child, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-2 bg-white rounded-lg border text-sm"
                      >
                        <div>
                          <span className="font-medium">{child.child_name}</span>
                          <span className="text-muted-foreground ml-2">({child.classroom})</span>
                        </div>
                        <div className="text-muted-foreground">
                          Parent: {child.parent_name} - {child.parent_position}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!employeeChildrenCompliance.is_compliant && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    You exceed the 40% limit by {(employeeChildrenCompliance.percentage - 40).toFixed(1)}%. 
                    The local agency may refuse to issue an authorization to this provider location.
                  </AlertDescription>
                </Alert>
              )}
              
              <p className="text-xs text-muted-foreground italic">
                To mark children as employee children, go to Children → click child → Edit → check "Employee's Child"
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6 [&>*]:min-w-0">
          {/* Input Form */}
          <Card className="card-clay">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                Ratio Calculator
              </CardTitle>
              <CardDescription>
                Enter classroom and children information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Classroom Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="classroom_name">Classroom Name</Label>
                  <Input
                    id="classroom_name"
                    value={formData.classroom_name}
                    onChange={(e) => setFormData({...formData, classroom_name: e.target.value})}
                    placeholder="Mixture Classroom"
                    data-testid="classroom-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    placeholder="MM/DD/YYYY"
                    data-testid="date-input"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="time">Time</Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      data-testid="time-input"
                    />
                  </div>
                  <div className="w-20">
                    <Label>AM/PM</Label>
                    <Select value={formData.am_pm} onValueChange={(v) => setFormData({...formData, am_pm: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AM">AM</SelectItem>
                        <SelectItem value="PM">PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Staff Info */}
              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium text-sm">Staff Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="teacher_name" className="text-xs">Teacher Name</Label>
                    <Input
                      id="teacher_name"
                      value={formData.teacher_name}
                      onChange={(e) => setFormData({...formData, teacher_name: e.target.value})}
                      className="h-9"
                      data-testid="teacher-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="teacher_hours" className="text-xs">Hours Worked</Label>
                    <Input
                      id="teacher_hours"
                      value={formData.teacher_hours}
                      onChange={(e) => setFormData({...formData, teacher_hours: e.target.value})}
                      placeholder="8:00 AM - 4:00 PM"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="second_teacher" className="text-xs">Second Teacher/Assistant</Label>
                    <Input
                      id="second_teacher"
                      value={formData.second_teacher_name}
                      onChange={(e) => setFormData({...formData, second_teacher_name: e.target.value})}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label htmlFor="second_hours" className="text-xs">Hours Worked</Label>
                    <Input
                      id="second_hours"
                      value={formData.second_teacher_hours}
                      onChange={(e) => setFormData({...formData, second_teacher_hours: e.target.value})}
                      placeholder="8:00 AM - 4:00 PM"
                      className="h-9"
                    />
                  </div>
                </div>
              </div>

              {/* Children by Age */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Baby className="w-4 h-4" />
                  Children by Age Group
                </h4>
                <div className="space-y-2">
                  {AGE_GROUPS.map((group) => (
                    <div 
                      key={group.id} 
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div className="flex-1">
                        <span className="font-medium text-sm">{group.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          (weight: {group.weight})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleChildCountChange(group.id, formData.children_by_age[group.id] - 1)}
                          disabled={formData.children_by_age[group.id] <= 0}
                        >
                          -
                        </Button>
                        <Input
                          type="number"
                          min="0"
                          value={formData.children_by_age[group.id]}
                          onChange={(e) => handleChildCountChange(group.id, e.target.value)}
                          className="w-16 h-8 text-center"
                          data-testid={`children-${group.id}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleChildCountChange(group.id, formData.children_by_age[group.id] + 1)}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Summary */}
              <div className="grid grid-cols-3 gap-3 p-4 bg-primary/5 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{getTotalChildren()}</p>
                  <p className="text-xs text-muted-foreground">Total Children</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{getTotalWeight()}</p>
                  <p className="text-xs text-muted-foreground">Total Weight</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{getQuickStaffEstimate()}</p>
                  <p className="text-xs text-muted-foreground">Staff Needed</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={calculateRatio} 
                  disabled={loading || getTotalChildren() === 0}
                  className="flex-1"
                  data-testid="calculate-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Ratio
                </Button>
                <Button 
                  variant="outline"
                  onClick={downloadPDF}
                  disabled={getTotalChildren() === 0}
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-6">
            {/* Calculation Result */}
            {result && (
              <Card className={`card-clay border-2 ${result.is_compliant ? 'border-green-300' : 'border-red-300'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading flex items-center gap-2">
                    {result.is_compliant ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    )}
                    Calculation Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex justify-center">
                    <Badge 
                      className={`text-lg px-4 py-2 ${
                        result.is_compliant 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {result.is_compliant ? '✓ COMPLIANT' : '✗ NOT COMPLIANT'}
                    </Badge>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-blue-700">{result.total_numerical_weight}</p>
                      <p className="text-sm text-blue-600">Total Weight</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-purple-700">{result.required_staff}</p>
                      <p className="text-sm text-purple-600">Staff Required</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-amber-700">{result.total_children}</p>
                      <p className="text-sm text-amber-600">Total Children</p>
                    </div>
                    <div className="p-4 bg-teal-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-teal-700">{result.max_group_size}</p>
                      <p className="text-sm text-teal-600">Max Group Size</p>
                    </div>
                  </div>

                  {/* Breakdown */}
                  {result.breakdown && result.breakdown.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Weight Breakdown</h4>
                      <div className="space-y-1">
                        {result.breakdown.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm p-2 bg-muted/30 rounded">
                            <span>{AGE_GROUPS.find(g => g.id === item.age_group)?.label || item.age_group}</span>
                            <span className="font-mono">
                              {item.children} × {item.numerical_weight} = {item.weight_in_group}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {result.notes && result.notes.length > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <ul className="list-disc list-inside space-y-1">
                          {result.notes.map((note, idx) => (
                            <li key={idx}>{note}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Info */}
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>• {result.has_infants_toddlers ? 'Infants/toddlers present - max group size is 8' : 'No infants/toddlers in group'}</p>
                    <p>• Classroom: {result.classroom_name}</p>
                    <p>• Time: {result.time}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* DCF Rules Reference */}
            <Card className="card-clay">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  {dcfMode === '250' ? 'DCF Family Child Care Rules (DCF 250.04)' : 'DCF Ratio Rules (DCF 251.055(2))'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {dcfMode === '250' ? (
                  <>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="font-medium text-green-800">Family Capacity Limits</p>
                      <ul className="text-green-700 text-xs mt-1 space-y-1">
                        <li>• Maximum 8 children present at any time</li>
                        <li>• Maximum 3 children under age 2</li>
                        <li>• Maximum 6 non-kindergarten children under age 6</li>
                      </ul>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="font-medium text-amber-800">Counting Rules</p>
                      <ul className="text-amber-700 text-xs mt-1 space-y-1">
                        <li>• The provider&apos;s own children under age 7 count when present</li>
                        <li>• A second qualified adult is required whenever more than 4 children are present</li>
                        <li>• Related and unrelated children both count toward the limit</li>
                      </ul>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                      Use the <strong>DCF 251 tab</strong> if your center is licensed as a group child care
                      (commercial building). The numerical weight formula does not apply to family child care.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="font-medium text-green-800">Staff Calculation</p>
                      <p className="text-green-700 text-xs mt-1">
                        One staff member required for a group with total numerical weight ≤ 1.04.
                        Weights below 0.05 are statistically insignificant.
                      </p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="font-medium text-amber-800">Group Size Limits</p>
                      <ul className="text-amber-700 text-xs mt-1 space-y-1">
                        <li>• Infants/toddlers present: max 8 children</li>
                        <li>• All school-age (5+): max 36 children</li>
                        <li>• Groups requiring 3+ staff must be split</li>
                      </ul>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="font-medium text-blue-800">Numerical Weights</p>
                      <div className="grid grid-cols-2 gap-1 text-xs text-blue-700 mt-1">
                        <span>Birth-18m: 0.25</span>
                        <span>18m-2.5y: 0.143</span>
                        <span>2.5 years: 0.125</span>
                        <span>3 years: 0.10</span>
                        <span>4 years: 0.077</span>
                        <span>5+ years: 0.056</span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RatioWorksheetPage;
